// src/app/components/loan/loan-detail/loan-detail.component.ts
import { Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterLink } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap, forkJoin, of, catchError } from 'rxjs';
import { LoanService } from '../../../services/loan.service';
import { AuthService } from '../../../services/auth/auth.service';
import { PdfModalComponent } from '../pdf-modal/pdf-modal.component';
import { getStatementDownloadUrl } from '../../../shared/utils/api-urls.utils';

@Component({
  selector: 'app-loan-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatCardModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './loan-detail.component.html',
  styleUrls: ['./loan-detail.component.scss']
})
export class LoanDetailComponent {
  private readonly loanService = inject(LoanService);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  readonly id = input.required<string>();
  readonly #id$ = toObservable(this.id);

  // Use 'ALL' string so the select option always shows the chosen label
  selectedYear: number | 'ALL' = 'ALL';

  // Compute distinct years from all statements
  readonly statementYears = computed(() => {
    const stmts = this.statements()?.statements || [];
    const years = Array.from(new Set(
      stmts.map(s => new Date(s.statementDate).getFullYear())
    ));
    return years.sort((a, b) => b - a);
  });

  // Filter statements by selected year or show all
  filteredStatements() {
    const stmts = this.statements()?.statements || [];
    if (this.selectedYear === 'ALL') {
      return stmts;
    }
    return stmts.filter(s => new Date(s.statementDate).getFullYear() === this.selectedYear);
  }

  readonly details = computed(toSignal(
    this.#id$.pipe(
      switchMap(id => id ? this.loanService.getLoanDetails(id) : of(null)),
      catchError(err => {
        console.error('[LOAN-DETAIL]: Failed to fetch loan details', err);
        return of(null);
      })
    ), { initialValue: null }
  ));

  readonly statements = computed(toSignal(
    this.#id$.pipe(
      switchMap(id => id ? this.loanService.getLoanStatements(id) : of(null)),
      catchError(err => {
        console.error('[LOAN-DETAIL]: Failed to fetch loan statements', err);
        return of(null);
      })
    ), { initialValue: null }
  ));

  /**
   * Fetch PDF blob with bearer token and open in modal
   */
  openStatementInModal(rowId: number) {
    const token = this.auth.token;
    const loanId = this.id();
    const url = getStatementDownloadUrl(loanId, rowId.toString());
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    // 1) detect mobile by UA
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile) {
      // hand off to native viewer in new tab
      this.http.get(url, { headers, responseType: 'blob' }).subscribe(blob => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      });
      return;
    }

 // 2) otherwise load into your PDF modal
    this.http.get(url, { headers, responseType: 'blob' }).subscribe(blob => {
      this.dialog.open(PdfModalComponent, {
        width: '90vw',
        height: '90vh',
        maxWidth: 'none',
        maxHeight: 'none',
        panelClass: 'pdf-dialog',
        data: { blob }
      });
    });
  }

  /**
   * Open the Mac Disclosure Statement & Account Customer Agreement in the specified image route
   */
  openTerms() {
    this.router.navigate(['static', 'mac-disclosure']);
  }
}
